import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProjects, createProject, updateProject, deleteProject } from '../utils/api';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, LayoutList } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = { name: '', type: '', contact: '', map_query: '', map_lat: '', map_lng: '', address: '' };

function ProjectModal({ project, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(project || EMPTY);
  const isEdit = !!project?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: isEdit ? (data) => updateProject(project.id, data) : createProject,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success(isEdit ? 'Project updated' : 'Project created');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.type) return toast.error('Name and type required');
    mutation.mutate({
      ...form,
      map_lat: form.map_lat ? parseFloat(form.map_lat) : null,
      map_lng: form.map_lng ? parseFloat(form.map_lng) : null,
    });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-head">
          <h3>{isEdit ? 'Edit Project' : 'Add New Project'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Project Name *</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Shiv Residency" required />
              </div>
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)} required>
                  <option value="">Select type</option>
                  <option>Residential Plots</option>
                  <option>Residential & Commercial</option>
                  <option>Commercial</option>
                  <option>Agricultural</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Contact Number</label>
                <input className="form-input" value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="9166523869" />
              </div>
              <div className="form-group">
                <label className="form-label">Map Search Query</label>
                <input className="form-input" value={form.map_query} onChange={e => set('map_query', e.target.value)} placeholder="Shiv+Residency+Jaipur" />
              </div>
              <div className="form-group">
                <label className="form-label">Latitude</label>
                <input className="form-input" type="number" step="any" value={form.map_lat} onChange={e => set('map_lat', e.target.value)} placeholder="26.7726" />
              </div>
              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input className="form-input" type="number" step="any" value={form.map_lng} onChange={e => set('map_lng', e.target.value)} placeholder="75.8394" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <textarea className="form-textarea" value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="Full project address..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-green btn-sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminProjects() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // null | 'new' | project object
  const { data: projects = [], isLoading } = useQuery({ queryKey: ['projects'], queryFn: getProjects });

  const delMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }); toast.success('Project deleted'); },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleDelete = (proj) => {
    if (!window.confirm(`Delete "${proj.name}"? This cannot be undone.`)) return;
    delMutation.mutate(proj.id);
  };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Projects</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 2 }}>Manage all real estate projects</p>
        </div>
        <button className="btn btn-green" onClick={() => setModal('new')}>
          <Plus size={16} /> Add Project
        </button>
      </div>

      <div className="card">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : projects.length === 0 ? (
          <div className="empty-state"><Building2 size={40} /><p>No projects yet. Add one to get started.</p></div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Total</th>
                  <th>Available</th>
                  <th>Booked</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.type}</td>
                    <td className="mono">{p.total}</td>
                    <td><span className="badge badge-available">{p.available}</span></td>
                    <td><span className="badge badge-booked">{p.booked}</span></td>
                    <td style={{ fontSize: 12 }}>📞 {p.contact}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Link to={`/admin/projects/${p.id}/plots`} className="btn btn-outline btn-sm btn-icon" title="Manage Plots">
                          <LayoutList size={14} />
                        </Link>
                        <button className="btn btn-outline btn-sm btn-icon" onClick={() => setModal(p)} title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button className="btn btn-sm btn-icon" onClick={() => handleDelete(p)} title="Delete"
                          style={{ background: 'var(--red-light)', color: 'var(--red)', border: 'none' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <ProjectModal
          project={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
